import { useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { AlertTriangle, ArrowLeft, ArrowUpRight, Check, MessageCircle } from "lucide-react";
import { getProfile, PROFILES } from "../lib/profiles";
import { waLink } from "../lib/site";
import { FadeUp, MaskLines, Overline } from "../components/Motion";
import { Process } from "../components/Process";
import { FAQ } from "../components/FAQ";
import { Contact } from "../components/Contact";

const EASE = [0.22, 1, 0.36, 1];

const ProfileHero = ({ p }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} data-testid="profile-hero" className="relative pt-32 lg:pt-40 pb-16 overflow-hidden">
      <div className="absolute inset-0 hero-bg" />
      <div className="relative mx-auto max-w-[1360px] px-6 lg:px-10">
        <Link to="/" data-testid="profile-back-link" className="inline-flex items-center gap-2 text-sm text-[#C0C9DC] hover:text-[#E6C687] transition-colors duration-300"><ArrowLeft className="w-4 h-4" /> Início</Link>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 items-end">
          <div className="lg:col-span-8">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono text-xs text-[#D4AF37]">{p.index} — {p.title}</motion.p>
            <MaskLines testid="profile-title" lines={p.heroLines} delay={0.25} className="mt-6 font-display text-[42px] leading-[0.98] sm:text-6xl lg:text-[84px] text-white" />
          </div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }} className="lg:col-span-4">
            <p className="text-base md:text-lg text-[#C0C9DC] leading-relaxed">{p.intro}</p>
            <a href={waLink(`Olá! Vim pelo site e preciso de ajuda com: ${p.title}.`)} target="_blank" rel="noreferrer" data-testid="profile-whatsapp-btn" className="btn-gold mt-8"><MessageCircle className="w-4 h-4" /> Analisar meu caso</a>
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ clipPath: "inset(30% 12% 30% 12%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.4 }}
        className="relative mx-auto mt-16 max-w-[1360px] px-6 lg:px-10"
      >
        <div className="relative h-[46vh] min-h-[300px] lg:h-[62vh] overflow-hidden border border-[#D4AF37]/25">
          <motion.img style={{ y }} src={p.image} alt={p.title} className="absolute inset-0 h-[120%] -top-[10%] w-full object-cover" />
          <span className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/70 to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};

const Situations = ({ p }) => (
  <section data-testid="profile-situations" className="mx-auto max-w-[1360px] px-6 lg:px-10 py-20 md:py-28 grid gap-12 lg:grid-cols-12">
    <FadeUp className="lg:col-span-5">
      <Overline>Para quem é</Overline>
      <h2 className="mt-6 font-display text-4xl sm:text-5xl text-white leading-[1.05]">Você se identifica com alguma destas situações?</h2>
    </FadeUp>
    <div className="lg:col-span-7">
      {p.situations.map((s, i) => (
        <FadeUp key={s} delay={i * 0.06} className="flex items-start gap-5 py-6 border-b border-[#D4AF37]/15">
          <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/50 text-[#D4AF37]"><Check className="w-3.5 h-3.5" /></span>
          <p className="text-lg md:text-xl text-white">{s}</p>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Solutions = ({ p }) => (
  <section data-testid="profile-solutions" className="bg-[#080E21] border-y border-[#D4AF37]/15">
    <div className="mx-auto max-w-[1360px] px-6 lg:px-10 py-20 md:py-28">
      <FadeUp>
        <Overline>Como podemos ajudar</Overline>
        <h2 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl text-white leading-[1.05]">Negociação e defesa, <span className="italic text-gold-gradient">lado a lado.</span></h2>
      </FadeUp>
      <div className="mt-14 grid gap-4 sm:grid-cols-2">
        {p.solutions.map((s, i) => (
          <FadeUp key={s.t} delay={i * 0.08}>
            <div data-testid={`solution-card-${i}`} className="group h-full border border-[#D4AF37]/20 bg-[#111D3A] p-8 md:p-10 transition-[border-color,transform] duration-500 hover:border-[#D4AF37]/60 hover:-translate-y-1">
              <span className="font-mono text-xs text-[#D4AF37]">0{i + 1}</span>
              <h3 className="mt-5 font-display text-3xl text-white">{s.t}</h3>
              <p className="mt-4 text-base text-[#C0C9DC] leading-relaxed">{s.d}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.1} className="mt-10">
        <div data-testid="profile-alert" className="flex flex-col sm:flex-row gap-5 border-l-2 border-[#D4AF37] bg-[#D4AF37]/[0.07] p-6 md:p-8">
          <AlertTriangle className="w-6 h-6 shrink-0 text-[#D4AF37]" />
          <p className="text-base md:text-lg text-[#E9EDF5] leading-relaxed">{p.alert}</p>
        </div>
      </FadeUp>
    </div>
  </section>
);

const OtherProfiles = ({ current }) => (
  <section data-testid="other-profiles" className="mx-auto max-w-[1360px] px-6 lg:px-10 pb-10">
    <Overline>Outros perfis</Overline>
    <div className="mt-8 grid gap-4 md:grid-cols-3">
      {PROFILES.filter((x) => x.slug !== current).map((x) => (
        <Link key={x.slug} to={`/perfil/${x.slug}`} data-testid={`other-profile-${x.slug}`} className="group flex items-center justify-between border border-[#D4AF37]/20 p-6 hover:bg-[#111D3A] transition-colors duration-500">
          <span>
            <span className="block font-mono text-xs text-[#D4AF37]">{x.index}</span>
            <span className="block mt-2 font-display text-2xl text-white">{x.label}</span>
          </span>
          <ArrowUpRight className="w-5 h-5 text-[#E6C687] transition-transform duration-500 group-hover:rotate-45" />
        </Link>
      ))}
    </div>
  </section>
);

export default function ProfilePage() {
  const { slug } = useParams();
  const p = getProfile(slug);
  if (!p) return <Navigate to="/" replace />;
  return (
    <main data-testid={`profile-page-${p.slug}`} key={p.slug}>
      <ProfileHero p={p} />
      <Situations p={p} />
      <Solutions p={p} />
      <Process title="Do contato à solução" />
      <FAQ items={p.faqs} title={`Dúvidas sobre ${p.label.toLowerCase()}`} />
      <Contact defaultProfile={p.slug} />
      <OtherProfiles current={p.slug} />
    </main>
  );
}
